import { Box, type BoxProps, Button, Heading, HStack, Stack, Text, VStack } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { faBadgeCheck, faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import countryCodeToFlagEmoji from '@/utils/countryCodeToFlagEmoji'
import { pluralize } from '@/utils/helpers'

import UserQ from '@/queries/UserQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import type { Query } from '@/client/query'
import * as url from '@/client/services/url'
import hasUserRole from '@/client/services/user/hasUserRole'
import { isLevelAtLeastMinLevel } from '@/client/services/worlds'

import ShwLink from '@/components/ShwLink'
import UserAvatar from '@/components/UserAvatar'
import type { UserDetails } from '@/components/Worlds/types'
import Card from '@/components/Worlds/UI/Card'
import LevelFlair from '@/components/Worlds/UI/Level/LevelFlair'

export default function SeriesHostCard({
  host,
  worldSlug,
  hasFixedWidth = false,
  ...props
}: {
  host: Query
  worldSlug: string
  hasFixedWidth?: boolean
} & BoxProps) {
  const userId = host(UserQ.id) ?? 0
  const username = host(UserQ.username) ?? null
  const displayName = host(UserQ.display_name) ?? ''
  const avatarSrc = host(UserQ.avatar_url) ?? ''
  const progressionLevel = host(UserQ.progression_level)
  const country = host(UserQ.country) ?? ''
  const bio = host(UserQ.bio) ?? ''
  const roles = host(UserQ.roles) ?? []
  const worldLevel = host(WorldMemberQ.level) ?? null
  const certificationCount = host(UserQ.countCertifications(UserQ.id)) ?? 0

  const user = { id: userId, roles } satisfies Pick<UserDetails, 'id' | 'roles'>

  const showLevelFlair =
    isLevelAtLeastMinLevel({ level: worldLevel, minLevel: 'senior-tutor' }) ||
    hasUserRole(user, 'admin:super') ||
    hasUserRole(user, 'admin:intern')

  return (
    <Card
      overflow="hidden"
      p="0"
      gap="0px"
      width={hasFixedWidth ? '334px' : undefined}
      flexShrink={hasFixedWidth ? 0 : undefined}
      flexGrow={1}
      display="flex"
      flexDirection="column"
      {...props}
    >
      <HStack px="16px" pt="16px" gap="16px" alignItems="stretch">
        <UserAvatar
          size="lg"
          src={avatarSrc}
          name={displayName}
          progressionLevel={progressionLevel}
        />
        <HStack alignItems="center" justifyContent="space-between" flexGrow={1} flexWrap="wrap">
          <VStack gap={0} alignItems="stretch" justifyContent="center" flexGrow={1}>
            <Stack
              gap={hasFixedWidth ? '4px' : { base: '4px', md: '8px' }}
              direction={hasFixedWidth ? 'column' : { base: 'column', md: 'row' }}
            >
              <Heading size="lg">
                {displayName} {countryCodeToFlagEmoji(country)}
              </Heading>
              {showLevelFlair ? (
                <LevelFlair user={user} worldSlug={worldSlug} level={worldLevel} />
              ) : null}
            </Stack>
            {certificationCount > 0 ? (
              <HStack gap="4px">
                <FontAwesomeIcon
                  icon={faBadgeCheck}
                  color={chakraTokenToVar('fg.subtle')}
                  size="sm"
                />
                <Text fontSize="xs" color="fg.muted">
                  Certified in {pluralize(certificationCount, 'topic')}
                </Text>
              </HStack>
            ) : null}
            <ShwLink href={url.getUserUrl({ userId, username })}>
              <Stack
                align="center"
                color="primary.fg"
                direction="row"
                spacing={2}
                display={hasFixedWidth ? 'flex' : { base: 'flex', md: 'none' }}
              >
                <Text as="span" fontWeight="bold" color="primary.fg" fontSize="sm">
                  View Profile
                </Text>
                <FontAwesomeIcon
                  style={{ fontWeight: 900, fontSize: '12px' }}
                  icon={faChevronRight}
                />
              </Stack>
            </ShwLink>
          </VStack>
          <ShwLink href={url.getUserUrl({ userId, username })}>
            <Button
              mr="5px"
              variant="outline"
              display={hasFixedWidth ? 'none' : { base: 'none', md: 'block' }}
            >
              View Profile
            </Button>
          </ShwLink>
        </HStack>
      </HStack>
      {bio !== '' ? (
        <Box
          width="calc(100% - 40px)"
          mx="20px"
          mb="20px"
          borderRadius="8px"
          bg="bg.subtle"
          border="1px solid"
          borderColor="border"
          position="relative"
          mt="25px"
          flexGrow={1}
          // This CSS is for the little triangle at the top of the tutor
          // description that makes it resemble a chat bubble!
          css={css`
            &::before {
                content: '';
                width: 0;
                height: 0;
                border-left: 15px solid transparent;
                border-right: 15px solid transparent;

                border-bottom: 15px solid ${chakraTokenToVar('border')};
                position: absolute;
                top: -15px;
                left: 48px;
                transform: translateX(-50%) scaleX(0.8);
            }

            &::after {
                content: '';
                width: 0;
                height: 0;
                border-left: 13px solid transparent;
                border-right: 13px solid transparent;

                border-bottom: 13px solid ${chakraTokenToVar('bg.subtle')};
                position: absolute;
                top: -13px;
                left: 48px;
                transform: translateX(-50%) scaleX(0.8);
            }
        `}
        >
          <VStack
            p="16px"
            gap="16px"
            alignItems="stretch"
            height="100%"
            justifyContent="space-between"
          >
            <Text size="md" noOfLines={5}>
              {bio}
            </Text>
          </VStack>
        </Box>
      ) : null}
    </Card>
  )
}
